import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { resolveImg } from "../lib/img";
import PlaceCard from "../components/PlaceCard";
import EventCard from "../components/EventCard";
import WalkCard from "../components/WalkCard";
import SEO, { breadcrumbLD, SITE_URL } from "../components/SEO";

export default function DestinationDetail() {
    const { slug } = useParams();
    const [city, setCity] = useState(null);
    const [places, setPlaces] = useState([]);
    const [events, setEvents] = useState([]);
    const [walks, setWalks] = useState([]);
    const [experiences, setExperiences] = useState([]);
    const [parcours, setParcours] = useState([]);
    const [error, setError] = useState("");

    useEffect(() => {
        setCity(null);
        api.get(`/cities/${slug}`)
            .then((r) => setCity(r.data))
            .catch(() => setError("Ville introuvable."));
        api.get("/places", { params: { city: slug, limit: 100 } }).then((r) => setPlaces(r.data)).catch(() => {});
        api.get("/events", { params: { city: slug, limit: 6 } }).then((r) => setEvents(r.data)).catch(() => {});
        api.get("/walks", { params: { city: slug, limit: 12 } }).then((r) => setWalks(r.data)).catch(() => {});
        api.get("/experiences", { params: { city: slug, limit: 12 } }).then((r) => setExperiences(r.data)).catch(() => {});
        api.get("/parcours", { params: { city: slug, limit: 6 } }).then((r) => setParcours(r.data)).catch(() => {});
    }, [slug]);

    if (error) {
        return (
            <div className="pt-40 px-6 md:px-12 max-w-[1600px] mx-auto text-center pb-40">
                <p className="cp-kicker text-cp-foret">Oups</p>
                <h1 className="cp-serif text-5xl mt-4">{error}</h1>
                <Link to="/destinations" className="cp-btn-ghost mt-10 inline-flex">Retour</Link>
            </div>
        );
    }

    if (!city) return <div className="pt-40 px-6 text-center text-cp-gris">Chargement…</div>;

    const region76 = ["le-treport", "eu", "criel-sur-mer", "mesnil-val", "berneval-le-grand",
                      "envermeu", "saint-martin-le-gaillard", "blangy-sur-bresle", "aumale",
                      "neufchatel-en-bray", "foucarmont"];
    const isNormandie = region76.includes(slug);
    const cityLD = {
        "@context": "https://schema.org",
        "@type": "City",
        "name": city.name,
        "description": (city.description || city.tagline || "").slice(0, 320),
        "image": city.hero_image ? (city.hero_image.startsWith("http") ? city.hero_image : `${SITE_URL}${city.hero_image}`) : undefined,
        "containedInPlace": {
            "@type": "AdministrativeArea",
            "name": isNormandie ? "Seine-Maritime · Normandie" : "Somme · Picardie",
        },
        "url": `${SITE_URL}/destinations/${slug}`,
    };

    return (
        <div data-testid={`destination-detail-${slug}`}>
            <SEO
                title={`${city.name} — guide & adresses vérifiées`}
                description={city.tagline || city.description?.slice(0, 200) || `${city.name} — guide complet Côte Perdue : adresses chic, balades, événements, hôtels vérifiés.`}
                image={city.hero_image}
                path={`/destinations/${slug}`}
                type="article"
                keywords={[city.name, isNormandie ? "Normandie" : "Picardie", isNormandie ? "Seine-Maritime" : "Somme", "Côte Perdue", "que faire", "week-end"]}
                structuredData={[
                    cityLD,
                    breadcrumbLD([
                        { name: "Accueil", path: "/" },
                        { name: "Destinations", path: "/destinations" },
                        { name: city.name, path: `/destinations/${slug}` },
                    ]),
                ]}
            />
            {/* HERO */}
            <section className="relative min-h-[85vh] overflow-hidden">
                <img src={resolveImg(city.hero_image)} alt={city.name} className="absolute inset-0 w-full h-full object-cover cp-kenburns" />
                <div className="absolute inset-0 cp-hero-overlay" />
                <div className="relative z-10 flex flex-col justify-end min-h-[85vh] px-6 md:px-12 py-20 max-w-[1600px] mx-auto">
                    <Link to="/destinations" className="cp-kicker text-cp-ivoire/70 hover:text-cp-ivoire transition-colors flex items-center gap-2 mb-8">
                        <ArrowLeft size={14} strokeWidth={1.5} /> Destinations
                    </Link>
                    <p className="cp-kicker text-cp-sable mb-4">0{city.position}</p>
                    <h1 className="cp-serif text-cp-ivoire text-5xl sm:text-6xl md:text-7xl lg:text-[92px] xl:text-[112px] leading-[0.98] tracking-[-0.025em]">
                        {city.name}
                    </h1>
                    <p className="text-cp-ivoire/90 text-xl md:text-2xl italic font-light mt-6 max-w-2xl">{city.tagline}</p>
                </div>
            </section>

            {city.images?.length > 0 && (
                <section className="px-6 md:px-12 max-w-[1600px] mx-auto pt-16 md:pt-24" data-testid="city-gallery">
                    <p className="cp-kicker text-cp-foret mb-8">Galerie</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                        {city.images.map((img, i) => (
                            <div key={i} className="aspect-[4/5] overflow-hidden bg-cp-craie">
                                <img src={resolveImg(img)} alt={`${city.name} — ${i + 1}`} loading="lazy" className="w-full h-full object-cover cp-photo-real hover:scale-105 transition-transform duration-700" />
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* INTRO */}
            <section className="px-6 md:px-12 max-w-[1400px] mx-auto py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                    <p className="cp-kicker text-cp-foret">À retenir</p>
                    <ul className="mt-6 flex flex-col gap-3">
                        {city.highlights?.map((h, i) => (
                            <li key={i} className="text-cp-noir text-base font-light flex gap-3">
                                <span className="text-cp-foret">—</span> {h}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="md:col-span-8">
                    <p className="text-cp-noir text-xl md:text-2xl font-light leading-relaxed whitespace-pre-line">
                        {city.description}
                    </p>
                </div>
            </section>

            {(experiences.length > 0 || walks.length > 0 || parcours.length > 0) && (
                <section className="px-6 md:px-12 max-w-[1600px] mx-auto pt-4 pb-16 md:pb-24" data-testid="destination-activities">
                    <div className="flex items-baseline justify-between gap-6 mb-10">
                        <h2 className="cp-serif text-4xl md:text-5xl leading-tight">À faire à {city.name}</h2>
                        <span className="cp-tiny text-cp-gris">{experiences.length + walks.length + parcours.length} activité{(experiences.length + walks.length + parcours.length) > 1 ? "s" : ""}</span>
                    </div>
                    <div className="space-y-12">
                        {experiences.length > 0 && (
                            <div data-testid="activities-experiences">
                                <p className="cp-kicker text-cp-foret mb-6">Expériences <span className="text-cp-gris/70 normal-case tracking-normal">· {experiences.length}</span></p>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                                    {experiences.map((x) => (
                                        <Link key={x.slug} to={`/experiences/${x.slug}`} className="group block" data-testid={`experience-card-${x.slug}`}>
                                            <div className="aspect-[4/3] overflow-hidden bg-cp-craie mb-5">
                                                {x.image ? (
                                                    <img src={resolveImg(x.image)} alt={x.title} loading="lazy" className="w-full h-full object-cover cp-photo-real group-hover:scale-105 transition-transform duration-700" />
                                                ) : <PhotoPlaceholder label={x.title} />}
                                            </div>
                                            <p className="cp-tiny text-cp-terre mb-2">{x.category || "Expérience"}</p>
                                            <h3 className="cp-serif text-2xl leading-[1.1] group-hover:text-cp-foret transition">{x.title}</h3>
                                            {x.tagline && <p className="cp-serif italic text-base text-cp-gris mt-2 leading-snug">{x.tagline}</p>}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                        {walks.length > 0 && (
                            <div data-testid="activities-walks">
                                <p className="cp-kicker text-cp-foret mb-6">Balades & randonnées <span className="text-cp-gris/70 normal-case tracking-normal">· {walks.length}</span></p>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                                    {walks.map((w) => (
                                        <Link key={w.slug} to={`/balades/${w.slug}`} className="group block" data-testid={`walk-card-${w.slug}`}>
                                            <div className="aspect-[4/3] overflow-hidden bg-cp-craie mb-5">
                                                {w.image || w.hero_image ? (
                                                    <img src={resolveImg(w.image || w.hero_image)} alt={w.title} loading="lazy" className="w-full h-full object-cover cp-photo-real group-hover:scale-105 transition-transform duration-700" />
                                                ) : <PhotoPlaceholder label={w.title} />}
                                            </div>
                                            <p className="cp-tiny text-cp-terre mb-2">{w.theme || "Balade"}</p>
                                            <h3 className="cp-serif text-2xl leading-[1.1] group-hover:text-cp-foret transition">{w.title}</h3>
                                            {w.intro && <p className="cp-serif italic text-base text-cp-gris mt-2 leading-snug">{String(w.intro).slice(0, 140)}{w.intro.length > 140 ? "…" : ""}</p>}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                        {parcours.length > 0 && (
                            <div data-testid="activities-parcours">
                                <p className="cp-kicker text-cp-foret mb-6">Parcours thématiques <span className="text-cp-gris/70 normal-case tracking-normal">· {parcours.length}</span></p>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                                    {parcours.map((p) => (
                                        <Link key={p.slug} to={`/parcours/${p.slug}`} className="group block" data-testid={`parcours-card-${p.slug}`}>
                                            <div className="aspect-[4/3] overflow-hidden bg-cp-craie mb-5">
                                                {p.image ? (
                                                    <img src={resolveImg(p.image)} alt={p.title} loading="lazy" className="w-full h-full object-cover cp-photo-real group-hover:scale-105 transition-transform duration-700" />
                                                ) : <PhotoPlaceholder label={p.title} />}
                                            </div>
                                            <p className="cp-tiny text-cp-terre mb-2">{p.theme || "Parcours"}</p>
                                            <h3 className="cp-serif text-2xl leading-[1.1] group-hover:text-cp-foret transition">{p.title}</h3>
                                            {p.tagline && <p className="cp-serif italic text-base text-cp-gris mt-2 leading-snug">{p.tagline}</p>}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {places.length > 0 && (() => {
                const CATEGORY_ORDER = [
                    { key: "hotel", label: "Hôtels" },
                    { key: "maison-hotes", label: "Maisons d'hôtes" },
                    { key: "restaurant", label: "Restaurants" },
                    { key: "cafe", label: "Cafés" },
                    { key: "boulangerie", label: "Boulangeries & épiceries" },
                    { key: "boisson", label: "Caves & bars" },
                    { key: "pub", label: "Pubs" },
                    { key: "patrimoine", label: "Patrimoine" },
                    { key: "musee", label: "Musées" },
                    { key: "jardin", label: "Jardins" },
                    { key: "marche", label: "Marchés" },
                    { key: "design", label: "Design & déco" },
                    { key: "spa", label: "Spa & bien-être" },
                ];
                const buckets = {};
                for (const p of places) {
                    const k = p.category || "autre";
                    (buckets[k] = buckets[k] || []).push(p);
                }
                const sections = CATEGORY_ORDER.filter(c => buckets[c.key]?.length)
                    .map(c => ({ label: c.label, items: buckets[c.key] }));
                const known = new Set(CATEGORY_ORDER.map(c => c.key));
                const other = Object.entries(buckets)
                    .filter(([k]) => !known.has(k))
                    .flatMap(([, arr]) => arr);
                if (other.length) sections.push({ label: "Autres", items: other });

                return (
                    <section className="px-6 md:px-12 max-w-[1600px] mx-auto py-16 md:py-24" data-testid="destination-places">
                        <div className="flex items-baseline justify-between gap-6 mb-10">
                            <h2 className="cp-serif text-4xl md:text-5xl leading-tight">Les adresses à {city.name}</h2>
                            <span className="cp-tiny text-cp-gris">{places.length} adresse{places.length > 1 ? "s" : ""}</span>
                        </div>
                        <div className="space-y-14 md:space-y-20">
                            {sections.map(({ label, items }) => (
                                <div key={label} data-testid={`places-group-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                    <p className="cp-kicker text-cp-foret mb-6">{label} <span className="text-cp-gris/70 normal-case tracking-normal">· {items.length}</span></p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                                        {items.map((p, i) => <PlaceCard key={p.id || p.slug} place={p} index={i} />)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })()}

            {walks.length > 0 && (
                <section className="px-6 md:px-12 max-w-[1600px] mx-auto py-16 md:py-24 bg-cp-craie">
                    <h2 className="cp-serif text-4xl md:text-5xl leading-tight mb-12">Balades autour de {city.name}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {walks.map((w, i) => <WalkCard key={w.id} walk={w} index={i} />)}
                    </div>
                </section>
            )}

            {events.length > 0 && (
                <section className="px-6 md:px-12 max-w-[1600px] mx-auto py-16 md:py-24">
                    <h2 className="cp-serif text-4xl md:text-5xl leading-tight mb-12">Prochains événements</h2>
                    <div>{events.map((ev) => <EventCard key={ev.id} event={ev} variant="wide" />)}</div>
                </section>
            )}
        </div>
    );
}
